import { Instance, SnapshotOut, types } from "mobx-state-tree"
import { FeedingTrayControl } from "../../database"
import { withEnvironment } from "../extensions/with-environment"
import { withRootStore } from "../extensions/with-root-store"
import { withBaseModel } from "../extensions/with-base-model"
import SyncableModel from "../../database/SyncableModel"
import { BaseModelStoreProps } from "../base-model-store/base-model-store"
import _ from "lodash"
import JalaAnalytic from "../../utils/jala-analytic"

const FeedingTrayControlStoreProps = {
  ...BaseModelStoreProps,

  isSyncing: types.maybeNull(types.boolean),
  feedingTrays: types.optional(types.array(types.frozen()), []),
}

/**
 * Model description here for TypeScript hints.
 */
export const FeedingTrayControlStoreModel = types
  .model("FeedingTrayControlStore")
  .props(FeedingTrayControlStoreProps)
  .extend(withEnvironment)
  .extend(withRootStore)
  .extend(withBaseModel)
  .views((self) => ({})) // eslint-disable-line @typescript-eslint/no-unused-vars
  .actions((self) => ({
    set(key: keyof typeof FeedingTrayControlStoreProps, value: any) {
      if (self[key] === value) return
      self[key] = value as never;
    },
    init() {
      self.initModel();
    },
    async store(data) {
      if (self.rootStore.appStore?.isOffline)
        return this.storeOffline(data)
      return self.storeModel(FeedingTrayControl, data);
    },
    async storeOffline(data) {
      return self.storeModelOffline(FeedingTrayControl, data);
    },
    async storeMultiple(data) {
      if (self.rootStore.appStore?.isOffline)
        return this.storeMultipleOffline(data)
      return _.reduce(
        data,
        (promise, datum) => promise.then(() => this.store(datum)),
        Promise.resolve(true)
      ).then((response) => {
        JalaAnalytic.log('Mobile app: anco - add', { data });
        return response;
      });
    },
    async storeMultipleOffline(data) {
      return _.reduce(
        data,
        (promise, datum) => promise.then(() => this.storeOffline(datum)),
        Promise.resolve(true)
      ).then((response) => {
        JalaAnalytic.log('Mobile app: anco - add', { data });
        return response;
      });
    },
    async update(model: SyncableModel, data) {
      if (self.rootStore.appStore?.isOffline)
        return this.updateOffline(model, data)
      return self.updateModel(FeedingTrayControl, model, data);
    },
    async updateOffline(model: SyncableModel, data) {
      return self.updateModelOffline(FeedingTrayControl, model, data);
    },
    async updateMultiple(data: Array<{ model: SyncableModel, data }>) {
      if (self.rootStore.appStore?.isOffline)
        return this.updateMultipleOffline(data)
      return _.reduce(
        data,
        (promise,  { model, data }) => (
          promise.then(() => model && model.getKey() ? this.update(model, data) : this.store(data))
        ),
        Promise.resolve(true)
      ).then((response) => {
        JalaAnalytic.log('Mobile app: anco - edit', { data });
        return response;
      });
    },
    async updateMultipleOffline(data: Array<{ model: SyncableModel, data }>) {
      return _.reduce(
        data,
        (promise,  { model, data }) => (
          promise.then(() => model && model.getKey() ? this.updateOffline(model, data) : this.storeOffline(data))
        ),
        Promise.resolve(true)
      ).then((response) => {
        JalaAnalytic.log('Mobile app: anco - edit', { data });
        return response;
      });
    },
    async delete(model: SyncableModel) {
      if (self.rootStore.appStore?.isOffline)
        return this.deleteOffline(model)
      return self.deleteModel(FeedingTrayControl, model);
    },
    async deleteOffline(model: SyncableModel) {
      return self.deleteModelOffline(FeedingTrayControl, model);
    },
    async deleteMultiple(models: Array<SyncableModel>) {
      if (self.rootStore.appStore?.isOffline)
        return this.deleteMultipleOffline(models)
      return _.reduce(
        models,
        (promise,  model) => (
          promise.then(() => this.delete(model))
        ),
        Promise.resolve(true)
      ).then((response) => {
        JalaAnalytic.log('Mobile app: anco - delete', { data: models.map((m) => m.toArray()) });
        return response;
      });
    },
    async deleteMultipleOffline(models: Array<SyncableModel>) {
      return _.reduce(
        models,
        (promise,  model) => (
          promise.then(() => this.deleteOffline(model))
        ),
        Promise.resolve(true)
      ).then((response) => {
        JalaAnalytic.log('Mobile app: anco - delete', { data: models.map((m) => m.toArray()) });
        return response;
      });
    },
  })) // eslint-disable-line @typescript-eslint/no-unused-vars

type FeedingTrayControlStoreType = Instance<typeof FeedingTrayControlStoreModel>
export interface FeedingTrayControlStore extends FeedingTrayControlStoreType {}
type FeedingTrayControlStoreSnapshotType = SnapshotOut<typeof FeedingTrayControlStoreModel>
export interface FeedingTrayControlStoreSnapshot extends FeedingTrayControlStoreSnapshotType {}
export const createFeedingTrayControlStoreDefaultModel = () => types.optional(FeedingTrayControlStoreModel, {})
